import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Building2,
  Clock,
  HardHat,
  RefreshCw,
  TrendingUp,
  Truck,
  Warehouse,
} from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import { AreaChart, Bars, Donut, HBarList, WeekHeat } from '../components/charts.jsx';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Loading,
  Progress,
  Segmented,
  Stars,
  Stat,
  Tabs,
  cx,
} from '../components/ui.jsx';
import { useApi } from '../lib/hooks.js';
import { compactMoney, money, num, pct, titleCase } from '../lib/format.js';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function Reports() {
  const [months, setMonths] = useState('6');
  const [days, setDays] = useState('30');
  const [tab, setTab] = useState('revenue');

  const { data: revenue, loading: loadingRevenue } = useApi('/reports/revenue', { params: { months } });
  const { data: ops, loading: loadingOps } = useApi('/reports/operations');
  const { data: lumpers } = useApi('/reports/lumpers', { params: { days } });
  const { data: customers } = useApi('/reports/customers');
  const { data: sites } = useApi('/reports/sites');

  const monthly = revenue?.monthly || [];
  const totals = monthly.reduce(
    (acc, row) => ({
      revenue: acc.revenue + Number(row.revenue || 0),
      cost: acc.cost + Number(row.cost || 0),
      margin: acc.margin + Number(row.margin || 0),
      jobs: acc.jobs + Number(row.jobs || 0),
      pallets: acc.pallets + Number(row.pallets || 0),
    }),
    { revenue: 0, cost: 0, margin: 0, jobs: 0, pallets: 0 },
  );

  const leaderboard = lumpers?.leaderboard || [];
  const hourly = (ops?.hourly || []).map((row) => ({ label: `${row.hour}:00`, value: row.jobs }));
  const weekHeat = (ops?.hourly || []).flatMap((row) =>
    WEEKDAYS.map((_, index) => ({ weekday: index, hour: row.hour, value: Math.round(Number(row.jobs) / 7) })),
  );

  return (
    <>
      <PageHead
        title="How business is going"
        subtitle="The numbers behind the work: what we charged, what we paid out, and what is left."
        icon={BarChart3}
        actions={
          <Segmented
            options={[
              { value: '3', label: '3 months' },
              { value: '6', label: '6 months' },
              { value: '12', label: '12 months' },
            ]}
            value={months}
            onChange={setMonths}
          />
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label={`Charged over ${months} months`} icon={TrendingUp} value={compactMoney(totals.revenue)} tone="amber" foot={`${num(totals.jobs)} jobs`} />
        <Stat label="Paid to lumpers" icon={HardHat} value={compactMoney(totals.cost)} tone="blue" foot={totals.revenue ? `${pct((totals.cost / totals.revenue) * 100, 1)} of billings` : '—'} />
        <Stat label="Left over" icon={TrendingUp} value={compactMoney(totals.margin)} tone="green" foot={totals.revenue ? `${pct((totals.margin / totals.revenue) * 100, 1)} margin` : '—'} />
        <Stat label="Pallets unloaded" icon={Truck} value={num(totals.pallets)} tone="violet" foot={`${num(totals.pallets / Math.max(totals.jobs, 1), 0)} pallets per job`} />
      </div>

      <Tabs
        className="mb-20"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'revenue', label: 'Money', icon: TrendingUp },
          { value: 'operations', label: 'How the work went', icon: Activity },
          { value: 'lumpers', label: 'How the lumpers did', icon: HardHat },
          { value: 'customers', label: 'Customers and places', icon: Building2 },
        ]}
      />

      {tab === 'revenue' ? (
        <div className="col gap-20">
          <Card>
            <CardHead title="Money each month" subtitle={`Last ${months} months`} icon={TrendingUp} />
            <CardBody>
              {loadingRevenue && !monthly.length ? (
                <Loading rows={4} />
              ) : (
                <AreaChart
                  labels={monthly.map((row) => row.month)}
                  series={[
                    { key: 'revenue', label: 'Billed', color: '#ff8a1f', values: monthly.map((row) => row.revenue) },
                    { key: 'cost', label: 'Lumper pay', color: '#5aa9ff', values: monthly.map((row) => row.cost) },
                    { key: 'margin', label: 'Margin', color: '#34d399', values: monthly.map((row) => row.margin) },
                  ]}
                  height={280}
                  valueFormat={(value) => money(value)}
                />
              )}
            </CardBody>
          </Card>

          <div className="split">
            <Card>
              <CardHead title="Which jobs earn the most" subtitle="What customers pay us for" />
              <CardBody>
                <HBarList
                  rows={(revenue?.byService || []).map((row) => ({
                    label: titleCase(row.freightType),
                    value: row.revenue,
                  }))}
                  format={(value) => compactMoney(value)}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHead title="Which jobs leave the most" subtitle="Jobs that do not leave much behind" />
              <CardBody>
                <HBarList
                  tone="green"
                  rows={(revenue?.byService || []).map((row) => ({
                    label: titleCase(row.freightType),
                    value: row.margin,
                  }))}
                  format={(value) => compactMoney(value)}
                />
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHead title="How busy each day of the week is" subtitle="Plan crew availability around the peaks" />
            <CardBody>
              <Bars
                data={(revenue?.byWeekday || []).map((row) => ({ label: WEEKDAYS[row.weekday], value: row.jobs }))}
                height={160}
                tone="blue"
              />
            </CardBody>
          </Card>
        </div>
      ) : null}

      {tab === 'operations' ? (
        <div className="col gap-20">
          <div className="split">
            <Card>
              <CardHead title="What time of day we are busiest" subtitle="Dock activity peaks" icon={Clock} />
              <CardBody>
                <Bars data={hourly} height={180} tone="amber" />
              </CardBody>
            </Card>

            <Card>
              <CardHead title="Are our prices right?" icon={TrendingUp} />
              <CardBody>
                <div className="row gap-20" style={{ alignItems: 'center' }}>
                  <Donut
                    value={(1 - Number(ops?.rateCheck?.lossRate || 0)) * 100}
                    label={pct((1 - Number(ops?.rateCheck?.lossRate || 0)) * 100, 0)}
                    sublabel="profitable jobs"
                    tone="green"
                    size={128}
                  />
                  <div className="col gap-12 grow">
                    <div>
                      <span className="label">Avg bill</span>
                      <div className="num" style={{ fontSize: 'var(--fs-xl)', fontWeight: 640 }}>{money(ops?.rateCheck?.avgBill)}</div>
                    </div>
                    <div>
                      <span className="label">Avg lumper pay</span>
                      <div className="num" style={{ fontSize: 'var(--fs-xl)', fontWeight: 640 }}>{money(ops?.rateCheck?.avgPay)}</div>
                    </div>
                    <div>
                      <span className="label">Avg margin</span>
                      <div className="num" style={{ fontSize: 'var(--fs-xl)', fontWeight: 640, color: 'var(--green)' }}>
                        {money(ops?.rateCheck?.avgMargin)}
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHead title="Different kinds of job" subtitle="Hours, wait time and average bill by freight type" icon={Truck} />
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Freight type</th>
                    <th className="right">Jobs</th>
                    <th className="right">Avg hours</th>
                    <th className="right">Avg pallets</th>
                    <th className="right">Avg dock wait</th>
                    <th className="right">Avg bill</th>
                  </tr>
                </thead>
                <tbody>
                  {(ops?.byFreight || []).map((row) => (
                    <tr key={row.freightType}>
                      <td>
                        <Badge tone="muted" size="sm">{titleCase(row.freightType)}</Badge>
                      </td>
                      <td className="right num">{num(row.jobs)}</td>
                      <td className="right num">{Number(row.avgHours).toFixed(1)}</td>
                      <td className="right num">{Number(row.avgPallets).toFixed(1)}</td>
                      <td className="right num">{Math.round(Number(row.avgWait))} min</td>
                      <td className="right num">{money(row.avgBill)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="split">
            <Card>
              <CardHead title="Places that keep crews waiting" subtitle="Average check-in to unload start" icon={Warehouse} />
              <CardBody className="col gap-12">
                {(ops?.waitBySite || []).map((site) => (
                  <div key={site.id} className="meter-row">
                    <div className="grow">
                      <div className="row between mb-4">
                        <Link to={`/app/sites/${site.id}`} className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>
                          {site.name}
                        </Link>
                        <span className="num dim" style={{ fontSize: 'var(--fs-sm)' }}>{Math.round(Number(site.avgWait))} min</span>
                      </div>
                      <Progress value={(Number(site.avgWait) / 120) * 100} tone={Number(site.avgWait) > 60 ? 'red' : undefined} thin />
                    </div>
                  </div>
                ))}
                {!ops?.waitBySite?.length ? <span className="dim">Not enough wait data yet.</span> : null}
              </CardBody>
            </Card>

            <Card>
              <CardHead title="Where all the jobs ended up" subtitle="Every job we have ever had" icon={Activity} />
              <CardBody>
                <HBarList
                  rows={(ops?.byStatus || []).map((row) => ({ label: titleCase(row.status), value: row.jobs }))}
                  format={(value) => num(value)}
                  labelWidth={120}
                  tone="blue"
                />
              </CardBody>
            </Card>
          </div>
        </div>
      ) : null}

      {tab === 'lumpers' ? (
        <div className="col gap-20">
          <div className="grid grid-4">
            <Stat label="Ready to work" icon={HardHat} value={num(lumpers?.funnel?.active)} tone="green" foot={`${num(lumpers?.funnel?.applicants)} in the pipeline`} />
            <Stat label="Average rating" icon={HardHat} value={Number(lumpers?.funnel?.avgRating || 0).toFixed(2)} tone="amber" foot="active lumpers" />
            <Stat label="Turn up on time" icon={Clock} value={Math.round(Number(lumpers?.funnel?.avgOnTime || 0))} unit="%" tone="lime" foot="arrival reliability" />
            <Stat label="Paperwork problems" icon={Activity} value={num((lumpers?.funnel?.missingW9 || 0) + (lumpers?.funnel?.notCleared || 0))} tone="red" foot="missing W-9 or screen" />
          </div>

          <Card>
            <CardHead
              title="Who earned the most"
              subtitle={`Top earners over the last ${days} days`}
              icon={HardHat}
              actions={
                <Segmented
                  options={[
                    { value: '7', label: '7d' },
                    { value: '30', label: '30d' },
                    { value: '90', label: '90d' },
                  ]}
                  value={days}
                  onChange={setDays}
                />
              }
            />
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Lumper</th>
                    <th className="narrow">Tier</th>
                    <th className="right">Jobs</th>
                    <th className="right">Hours</th>
                    <th className="right">Avg hrs</th>
                    <th className="right">Avg wait</th>
                    <th className="narrow">Rating</th>
                    <th className="right">On-time</th>
                    <th className="right">Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.slice(0, 20).map((lumper) => (
                    <tr key={lumper.id}>
                      <td>
                        <div className="row gap-10">
                          <Avatar name={lumper.name} tone={lumper.photoColor || 'amber'} size="sm" />
                          <Link to={`/app/lumpers/${lumper.id}`} className="truncate" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                            {lumper.name}
                          </Link>
                        </div>
                      </td>
                      <td className="narrow"><Badge tone={lumper.tier === 'crew_lead' ? 'amber' : 'muted'} size="sm">{titleCase(lumper.tier)}</Badge></td>
                      <td className="right num">{num(lumper.jobs)}</td>
                      <td className="right num">{Number(lumper.hours).toFixed(1)}</td>
                      <td className="right num">{Number(lumper.avgHours).toFixed(1)}</td>
                      <td className="right num">{Math.round(Number(lumper.avgWait))}m</td>
                      <td className="narrow">
                        {Number(lumper.jobRating) ? <Stars value={Number(lumper.jobRating)} size={11} /> : <span className="dimmer">—</span>}
                      </td>
                      <td className="right num">{Math.round(Number(lumper.onTimeRate))}%</td>
                      <td className="right num" style={{ fontWeight: 620 }}>{money(lumper.pay)}</td>
                    </tr>
                  ))}
                  {!leaderboard.length ? (
                    <tr><td colSpan={9}><EmptyState icon={HardHat} title="No completed work in this window" /></td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <CardHead title="Who turns up most reliably" subtitle="Highest on-time and acceptance rates" icon={Activity} />
            <CardBody className="col gap-12">
              {(lumpers?.reliability || []).map((lumper) => (
                <div key={lumper.id} className="meter-row">
                  <div className="grow">
                    <div className="row between mb-4">
                      <Link to={`/app/lumpers/${lumper.id}`} className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>
                        {lumper.name}
                      </Link>
                      <span className="num dim" style={{ fontSize: 'var(--fs-sm)' }}>
                        {Math.round(Number(lumper.onTimeRate))}% on-time · {Math.round(Number(lumper.acceptanceRate))}% accept
                      </span>
                    </div>
                    <Progress value={Number(lumper.onTimeRate)} tone={Number(lumper.onTimeRate) >= 95 ? 'lime' : undefined} thin />
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      ) : null}

      {tab === 'customers' ? (
        <div className="col gap-20">
          <Card>
            <CardHead title="Which customers are worth having" subtitle="Lifetime, verified work only" icon={Building2} />
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th className="narrow">Type</th>
                    <th className="narrow">Terms</th>
                    <th className="right">Jobs</th>
                    <th className="right">Revenue</th>
                    <th className="right">Margin</th>
                    <th className="right">Margin %</th>
                    <th className="right">Avg job</th>
                    <th className="right">Unfilled</th>
                    <th className="right">Open A/R</th>
                  </tr>
                </thead>
                <tbody>
                  {(customers?.rows || []).map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Link to={`/app/customers/${row.id}`} style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{row.name}</Link>
                      </td>
                      <td className="narrow"><Badge tone="muted" size="sm">{titleCase(row.kind)}</Badge></td>
                      <td className="narrow mono dim">{row.terms}</td>
                      <td className="right num">{num(row.jobs)}</td>
                      <td className="right num">{money(row.revenue)}</td>
                      <td className="right num" style={{ color: 'var(--green)' }}>{money(row.margin)}</td>
                      <td className="right num">{Number(row.revenue) ? pct((Number(row.margin) / Number(row.revenue)) * 100, 1) : '—'}</td>
                      <td className="right num">{money(row.avgJob)}</td>
                      <td className="right num" style={{ color: Number(row.unfilled) ? 'var(--amber)' : undefined }}>{num(row.unfilled)}</td>
                      <td className="right num" style={{ color: Number(row.balance) > 0 ? 'var(--amber)' : undefined }}>{money(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="split">
            <Card>
              <CardHead title="Revenue by account type" icon={Building2} />
              <CardBody>
                <HBarList
                  rows={(customers?.byKind || []).map((row) => ({ label: titleCase(row.kind), value: row.revenue }))}
                  format={(value) => compactMoney(value)}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHead title="Busiest warehouses" subtitle="Jobs we have sent there" icon={Warehouse} />
              <CardBody>
                <HBarList
                  tone="blue"
                  rows={(sites || []).slice(0, 10).map((row) => ({ label: `${row.name}`, value: row.jobs }))}
                  format={(value) => num(value)}
                  labelWidth={168}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      ) : null}
    </>
  );
}
